import type { ReactNode } from "react";
import { Link } from "react-router";
import { ShieldCheck } from "lucide-react";

import { Logo } from "@/components/common/logo";
import { FadeUp } from "@/components/common/motion";

/**
 * Split-screen shell shared by Login and Sign-up.
 *
 * Left: a full-bleed maroon plate carrying the white wordmark and the trust
 * copy. Right: cream canvas with the form, centred in a narrow measure.
 */
export function AuthLayout({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  panelTitle = "Your secure workspace",
  panelBody = "Access your HealNova clinician workspace to manage consultations, patients and appointments.",
}: {
  eyebrow?: string | undefined;
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  panelTitle?: string | undefined;
  panelBody?: string | undefined;
}) {
  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,0.62fr)_minmax(0,1fr)]">
      <aside className="relative hidden overflow-hidden bg-primary px-12 py-14 text-primary-foreground lg:flex lg:flex-col">
        <PanelWaves />

        <div className="relative z-10 flex flex-1 flex-col">
          <FadeUp duration={0.6}>
            <Link to="/" aria-label="HealNova home">
              <Logo onDark />
            </Link>
          </FadeUp>

          <div className="mt-auto max-w-sm">
            <FadeUp delay={0.1}>
              <h2 className="font-display text-4xl leading-[1.12] font-semibold text-balance">
                {panelTitle}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-primary-foreground/75">
                {panelBody}
              </p>
            </FadeUp>

            <FadeUp
              delay={0.2}
              className="mt-12 flex items-center gap-3.5 border-t border-primary-foreground/15 pt-8"
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-full border border-primary-foreground/25">
                <ShieldCheck className="size-5" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-sm font-semibold">HIPAA Compliant</span>
                <span className="mt-0.5 block text-xs text-primary-foreground/65">
                  Your data is safe and encrypted
                </span>
              </span>
            </FadeUp>
          </div>
        </div>
      </aside>

      <section className="relative flex flex-col overflow-hidden px-5 py-8 sm:px-10">
        <CanvasWaves />

        <div className="relative z-10 flex lg:hidden">
          <Link to="/" aria-label="HealNova home">
            <Logo />
          </Link>
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
          <FadeUp duration={0.6}>
            {eyebrow && (
              <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">
                {eyebrow}
              </p>
            )}
            <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-balance sm:text-[2.75rem]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                {subtitle}
              </p>
            )}
          </FadeUp>

          <FadeUp delay={0.12} className="mt-8">
            {children}
          </FadeUp>

          {footer && (
            <FadeUp delay={0.22} className="mt-8 text-center text-sm text-muted-foreground">
              {footer}
            </FadeUp>
          )}
        </div>
      </section>
    </div>
  );
}

/** Faint contour lines sweeping across the maroon plate. */
function PanelWaves() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 400 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hn-panel-fade" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="oklch(0.7088 0.1008 26.01)" stopOpacity="0" />
          <stop offset="55%" stopColor="oklch(0.7088 0.1008 26.01)" stopOpacity="0.26" />
          <stop offset="100%" stopColor="oklch(0.7088 0.1008 26.01)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 26, 52, 78, 104, 130].map((offset) => (
        <path
          key={offset}
          d={`M-60 ${600 + offset} C 90 ${510 + offset}, 210 ${720 + offset}, 460 ${550 + offset}`}
          fill="none"
          stroke="url(#hn-panel-fade)"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}

/** The same contours, barely there, on the cream side. */
function CanvasWaves() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-70"
      viewBox="0 0 600 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      {[0, 30, 60, 90].map((offset) => (
        <path
          key={`top-${offset}`}
          d={`M240 ${-60 + offset} C 380 ${60 + offset}, 470 ${140 + offset}, 700 ${120 + offset}`}
          fill="none"
          stroke="oklch(0.2786 0.0871 11.82 / 0.05)"
          strokeWidth="1"
        />
      ))}
      {[0, 26, 52, 78].map((offset) => (
        <path
          key={`bottom-${offset}`}
          d={`M-80 ${760 + offset} C 120 ${690 + offset}, 300 ${850 + offset}, 660 ${700 + offset}`}
          fill="none"
          stroke="oklch(0.2786 0.0871 11.82 / 0.05)"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
